import { Divider, Grid2 as Grid, List, ListItem, MenuItem, Select, Skeleton, Typography} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { rootContext } from "../contexts/rootContext";
import TransactionCard from "../components/TransactionCard";
import AllTransactionsSection from "../components/AllTransactionsSection";
import { getSaleHistory, getTransactions } from "../services/supabase";
import { SaleHistory, Transaction } from "../types";
import { getDateOptions, getSqlDateFormat } from "../utilities/dateHelper";
import SaleHistoryDialog from "../components/SaleHistoryDialog";

function Home() {
    const context = useContext(rootContext);    
    const dateOptions: Date[][] = getDateOptions();
    const [ selectedWeekIndex, setSelectedWeekIndex ] = useState(0);
    const [ transactions, setTransactions ] = useState<Transaction[]>([]);
    const [ loadingTransactions, setLoadingTransactions ] = useState(true);

    const [ dialogOpen, setDialogOpen ] = useState(false);
    const [ selectedTransaction, setSelectedTransaction ] = useState<Transaction>();
    const [ saleHistory, setSaleHistory ] = useState<SaleHistory[]>([]);
    const [ loadingSaleHistory, setLoadingSaleHistory] = useState(false);

    let largestTransaction: Transaction = {
        parcelNumber: "",
        saleDate: "",
        grantor: "",
        grantee: "",
        price: 0,
        deedType: "",
        Parcel: {
            streetAddress: "",
            parcelType: 0,
            acres: 0,
            city: "",
            zip: "",
            neighborhood: "",
            marketArea: ""
        }
    };

    let mostActiveMarketDict: {
        [key: string]: number;
    } = {};
    let mostActiveCityDict: {
        [key: string]: number;
    } = {};
    let mostActiveNeighborhoodDict: {
        [key: string]: number;
    } = {};
    let mostActiveZipDict: {
        [key: string]: number;
    } = {};

    transactions.forEach(transaction => {
        if (transaction.price > largestTransaction.price) {
            largestTransaction = transaction;
        }
        if (mostActiveMarketDict[transaction.Parcel.marketArea]) {
            mostActiveMarketDict[transaction.Parcel.marketArea]++;
        } else {
            mostActiveMarketDict[transaction.Parcel.marketArea] = 1;
        }
        if (mostActiveCityDict[transaction.Parcel.city]) {
            mostActiveCityDict[transaction.Parcel.city]++;
        } else {
            mostActiveCityDict[transaction.Parcel.city] = 1;
        }
        if (mostActiveNeighborhoodDict[transaction.Parcel.neighborhood]) {
            mostActiveNeighborhoodDict[transaction.Parcel.neighborhood]++;
        } else {
            mostActiveNeighborhoodDict[transaction.Parcel.neighborhood] = 1;
        }
        if (mostActiveZipDict[transaction.Parcel.zip]) {
            mostActiveZipDict[transaction.Parcel.zip]++;
        } else {
            mostActiveZipDict[transaction.Parcel.zip] = 1;
        }
    })

    let mostActiveMarketArea = "";
    let mostActiveMarketAreaCount = 0;
    let mostActiveCity = "";
    let mostActiveCityCount = 0;
    let mostActiveNeighborhood = "";
    let mostActiveNeighborhoodCount = 0;
    let mostActiveZip = "";
    let mostActiveZipCount = 0;

    for (const [key, value] of Object.entries(mostActiveMarketDict)) {
        if (value > mostActiveMarketAreaCount) {
            mostActiveMarketArea = key;
            mostActiveMarketAreaCount = value;
        }
    }
    for (const [key, value] of Object.entries(mostActiveCityDict)) {
        if (value > mostActiveCityCount) {
            mostActiveCity = key;
            mostActiveCityCount = value;
        }
    }
    for (const [key, value] of Object.entries(mostActiveNeighborhoodDict)) {
        if (value > mostActiveNeighborhoodCount) {
            mostActiveNeighborhood = key;
            mostActiveNeighborhoodCount = value;
        }
    }
    for (const [key, value] of Object.entries(mostActiveZipDict)) {
        if (value > mostActiveZipCount) {
            mostActiveZip = key;
            mostActiveZipCount = value;
        }
    }

    useEffect(()=>{
        setLoadingTransactions(true);
        getTransactions(
            getSqlDateFormat(dateOptions[selectedWeekIndex][0]),
            getSqlDateFormat(dateOptions[selectedWeekIndex][1]),
            (result)=> {
                setLoadingTransactions(false);
                setTransactions(result);
            },
            () => console.error("unable to load transactions")
        );
    }, [selectedWeekIndex])

    useEffect(()=>{
        if (selectedTransaction !== undefined) {
            setLoadingSaleHistory(true);
            getSaleHistory(
                selectedTransaction?.parcelNumber, 
                (result)=> {
                    setLoadingSaleHistory(false);
                    setSaleHistory(result);
                },
                () => console.error("unable to load sale history")
            );
        }
    }, [selectedTransaction])

    return (<>
        <Grid size="grow" sx={{margin: "25px"}}>
            <Typography variant="h4">Market Snapshot</Typography>
            <Select value={selectedWeekIndex} onChange={(event: any) => setSelectedWeekIndex(event.target.value)}>
                {dateOptions.map((option, index) =>
                    <MenuItem value={index}>{`${option[0].toLocaleDateString()} - ${option[1].toLocaleDateString()}`}</MenuItem>
                )}
            </Select>
            <Typography variant="subtitle2">Commercial Real Estate</Typography>
            <Typography variant="subtitle2" sx={{paddingBottom: "10px"}} >Pulaski County, AR</Typography>
            <Divider/>
            <Typography variant="h6" sx={{paddingTop: "10px"}}>Summary</Typography>
            <List>
                <ListItem>
                    <b>Total Transactions:</b>&nbsp;{loadingTransactions ?
                    <Skeleton variant="text" sx={{ fontSize: '1rem', width: "50px" }} />
                    :
                    transactions.length}
                </ListItem>
                <ListItem>
                    <b>Most Active Market Area:</b>&nbsp;{loadingTransactions ?
                    <Skeleton variant="text" sx={{ fontSize: '1rem', width: "50px" }} />
                    :
                    `${mostActiveMarketArea} (${mostActiveMarketAreaCount})`}
                </ListItem>
                <ListItem>
                    <b>Most Active City:</b>&nbsp;{loadingTransactions ?
                    <Skeleton variant="text" sx={{ fontSize: '1rem', width: "50px" }} />
                    :
                    `${mostActiveCity} (${mostActiveCityCount})`}
                </ListItem>
                <ListItem>
                    <b>Most Active Neighborhood:</b>&nbsp;{loadingTransactions ?
                    <Skeleton variant="text" sx={{ fontSize: '1rem', width: "50px" }} />
                    :
                    `${mostActiveNeighborhood} (${mostActiveNeighborhoodCount})`}
                </ListItem>
                <ListItem>
                    <b>Most Active Zipcode:</b>&nbsp;{loadingTransactions ?
                    <Skeleton variant="text" sx={{ fontSize: '1rem', width: "50px" }} />
                    :
                    `${mostActiveZip} (${mostActiveZipCount})`}
                </ListItem>
                <ListItem>
                    <b>Largest Transaction:</b>
                </ListItem>
                <ListItem>{ loadingTransactions ?
                            <Skeleton variant="rectangular" width="300px" height="350px" />
                            :
                            transactions.length > 0 ?
                                <TransactionCard 
                                    transaction={largestTransaction} 
                                    setDialogOpen={setDialogOpen} 
                                    setSelectedTransaction={setSelectedTransaction}
                                />
                                :
                                <></>}
                </ListItem>
            </List>
            <Divider/>
            <AllTransactionsSection 
                transactions={transactions} 
                loadingTransactions={loadingTransactions} 
                setDialogOpen={setDialogOpen} 
                setSelectedTransaction={setSelectedTransaction}
            />
        </Grid>
        <SaleHistoryDialog 
            open={dialogOpen}
            setOpen={setDialogOpen}
            selectedTransaction={selectedTransaction}
            saleHistory={saleHistory}
            loadingSaleHistory={loadingSaleHistory}
        />
        </>);
}

export default Home;